import { useCallback, useState } from 'react';

/** The pages column (ds/AppShell.tsx): its width in px, dragged from its right edge and remembered per browser. */
export const PAGE_TREE_WIDTH_DEFAULT = 280;
export const PAGE_TREE_WIDTH_MIN = 200;
export const PAGE_TREE_WIDTH_MAX = 560;
/** Arrow keys on the handle move by this; Shift multiplies it by four. */
export const PAGE_TREE_WIDTH_STEP = 16;
const STORAGE_KEY = 'ark.pageTree.width';

export function clampPageTreeWidth(width: number): number {
    if (!Number.isFinite(width)) return PAGE_TREE_WIDTH_DEFAULT;
    return Math.min(PAGE_TREE_WIDTH_MAX, Math.max(PAGE_TREE_WIDTH_MIN, Math.round(width)));
}

function readStored(): number | null {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw === null) return null;
        const value = Number(raw);
        return Number.isFinite(value) ? clampPageTreeWidth(value) : null;
    } catch {
        return null;
    }
}

function store(width: number | null) {
    try {
        if (width === null) window.localStorage.removeItem(STORAGE_KEY);
        else window.localStorage.setItem(STORAGE_KEY, String(width));
    } catch {
        // Private mode or blocked storage: the width lives for the session.
    }
}

/** `width` (clamped), `setWidth` (clamps and remembers), `reset` (the default, forgets). */
export function usePageTreeWidth() {
    const [width, setState] = useState(() => readStored() ?? PAGE_TREE_WIDTH_DEFAULT);
    const setWidth = useCallback((next: number) => {
        const value = clampPageTreeWidth(next);
        setState(value);
        store(value);
    }, []);
    const reset = useCallback(() => {
        setState(PAGE_TREE_WIDTH_DEFAULT);
        store(null);
    }, []);
    return { width, setWidth, reset };
}
